import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Smartphone, 
  Car, 
  Calendar, 
  Gauge, 
  Fuel, 
  Settings2, 
  ChevronRight,
  Search,
  RefreshCw,
  ExternalLink,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2
} from "lucide-react";

export default function MobileReportsPage() {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalyses = async () => {
    try {
      const r = await api.listSounds();
      // Sort by date descending (newest first)
      const sorted = (r.data || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setAnalyses(sorted);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchAnalyses(); }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalyses();
  };

  // Filter analyses based on search
  const filteredAnalyses = analyses.filter(a => {
    const searchLower = searchTerm.toLowerCase();
    const make = a.car_details?.make?.toLowerCase() || "";
    const model = a.car_details?.model?.toLowerCase() || "";
    const year = String(a.car_details?.year || "");
    const id = (a.id || "").toLowerCase();
    return make.includes(searchLower) || model.includes(searchLower) || year.includes(searchLower) || id.includes(searchLower);
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
            <CheckCircle2 size={12} />
            Completed
          </Badge>
        );
      case "error":
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200 gap-1">
            <AlertCircle size={12} />
            Error
          </Badge>
        );
      default:
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1">
            <Loader2 size={12} className="animate-spin" />
            Processing
          </Badge>
        );
    }
  };

  const getRatingColor = (grade) => {
    const colors = {
      A: "from-emerald-500 to-emerald-600",
      B: "from-sky-500 to-sky-600",
      C: "from-amber-500 to-amber-600",
      D: "from-orange-500 to-orange-600",
      F: "from-red-500 to-red-600"
    };
    return colors[grade] || "from-slate-500 to-slate-600";
  };

  const getRatingBgColor = (grade) => {
    const colors = {
      A: "bg-emerald-50 border-emerald-200",
      B: "bg-sky-50 border-sky-200",
      C: "bg-amber-50 border-amber-200",
      D: "bg-orange-50 border-orange-200",
      F: "bg-red-50 border-red-200"
    };
    return colors[grade] || "bg-slate-50 border-slate-200";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getFuelIcon = (fuelType) => {
    const fuel = fuelType?.toLowerCase() || "";
    if (fuel.includes("diesel")) return "🛢️";
    if (fuel.includes("petrol") || fuel.includes("gasoline")) return "⛽";
    if (fuel.includes("electric") || fuel.includes("ev")) return "🔋";
    if (fuel.includes("hybrid")) return "🔌";
    if (fuel.includes("cng")) return "💨";
    return "⛽";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
              <Smartphone size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900" data-testid="mobile-reports-title">
                Mobile App Reports
              </h1>
              <p className="text-base text-slate-500">
                View all vehicle analyses uploaded from mobile devices
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by make, model, year..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64 rounded-lg border-slate-200"
              data-testid="search-input"
            />
          </div>
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-lg"
            data-testid="refresh-btn"
          >
            <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Reports</p>
                <p className="text-2xl font-bold text-slate-900">{analyses.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Car size={20} className="text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {analyses.filter(a => a.status === "completed").length}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 size={20} className="text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Processing</p>
                <p className="text-2xl font-bold text-amber-600">
                  {analyses.filter(a => !["completed", "error"].includes(a.status)).length}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Loader2 size={20} className="text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Avg. Health</p>
                <p className="text-2xl font-bold text-sky-600">
                  {analyses.length > 0 
                    ? Math.round(analyses.filter(a => a.engine_rating?.overall_rating).reduce((sum, a) => sum + (a.engine_rating?.overall_rating || 0), 0) / analyses.filter(a => a.engine_rating?.overall_rating).length) || "—"
                    : "—"}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                <Gauge size={20} className="text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reports List */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CardHeader className="bg-slate-50 border-b border-slate-200 py-4">
          <CardTitle className="text-lg font-semibold text-slate-700 flex items-center gap-2">
            <Smartphone size={18} />
            All Vehicle Reports
            <Badge variant="secondary" className="ml-2">{filteredAnalyses.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-16 text-center">
              <Loader2 size={32} className="mx-auto text-slate-400 animate-spin mb-4" />
              <p className="text-slate-500">Loading reports...</p>
            </div>
          ) : filteredAnalyses.length === 0 ? (
            <div className="p-16 text-center" data-testid="empty-reports">
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Smartphone size={32} className="text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Reports Found</h3>
              <p className="text-slate-500 mb-6">
                {searchTerm 
                  ? "No reports match your search criteria" 
                  : "Upload your first vehicle analysis from the mobile app"}
              </p>
              {searchTerm && (
                <Button variant="outline" onClick={() => setSearchTerm("")}>
                  Clear Search
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredAnalyses.map((analysis) => (
                <Link 
                  key={analysis.id} 
                  to={`/analysis/${analysis.id}`}
                  className="block hover:bg-slate-50 transition-colors"
                  data-testid={`report-card-${analysis.id}`}
                >
                  <div className="p-4 md:p-5 flex items-center gap-4">
                    {/* Rating Circle */}
                    <div className={`w-16 h-16 rounded-xl flex flex-col items-center justify-center border-2 ${getRatingBgColor(analysis.engine_rating?.grade)}`}>
                      {analysis.status === "completed" && analysis.engine_rating ? (
                        <>
                          <span className={`text-xl font-bold bg-gradient-to-br ${getRatingColor(analysis.engine_rating?.grade)} bg-clip-text text-transparent`}>
                            {analysis.engine_rating?.grade}
                          </span>
                          <span className="text-xs text-slate-500 font-medium">
                            {analysis.engine_rating?.overall_rating}%
                          </span>
                        </>
                      ) : (
                        <Loader2 size={24} className="text-slate-400 animate-spin" />
                      )}
                    </div>

                    {/* Vehicle Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-slate-900 truncate">
                          {analysis.car_details?.make} {analysis.car_details?.model}
                        </h3>
                        {getStatusBadge(analysis.status)}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {analysis.car_details?.year}
                        </span>
                        <span className="flex items-center gap-1">
                          <Gauge size={14} />
                          {analysis.car_details?.km_driven?.toLocaleString()} km
                        </span>
                        <span className="flex items-center gap-1">
                          {getFuelIcon(analysis.car_details?.fuel_type)}
                          {analysis.car_details?.fuel_type}
                        </span>
                        <span className="flex items-center gap-1">
                          <Settings2 size={14} />
                          {analysis.car_details?.transmission}
                        </span>
                        {analysis.car_details?.has_turbo && (
                          <Badge variant="outline" className="text-xs py-0">Turbo</Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                        <Clock size={12} />
                        {formatDate(analysis.created_at)}
                        <span className="ml-2 font-mono bg-slate-100 px-1.5 py-0.5 rounded text-[10px] text-slate-500" title="Analysis ID">
                          ID: {analysis.id?.slice(0, 8)}
                        </span>
                        {analysis.from_cache && (
                          <Badge variant="secondary" className="text-[10px] ml-2 py-0">Cached</Badge>
                        )}
                      </div>
                    </div>

                    {/* Action */}
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-slate-400 hover:text-slate-600"
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(`/analysis/${analysis.id}`, '_blank');
                        }}
                      >
                        <ExternalLink size={16} />
                      </Button>
                      <ChevronRight size={20} className="text-slate-300" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile App Instructions */}
      <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-violet-50 to-purple-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0">
              <Smartphone size={24} className="text-violet-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">How to Upload from Mobile</h3>
              <ol className="text-sm text-slate-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <span>Open the WiseDrive mobile app and enter your vehicle details</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <span>Record a 15-30 second video of your running engine</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <span>Upload and wait for the AI analysis to complete</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                  <span>View your detailed report here in the portal!</span>
                </li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
